import { useAtomValue, useSetAtom } from 'jotai';
import { hasClickedApplyFinanceAtom } from '../Atoms/HasClickedApplyFinance.atom';
import {
  finCalcInteractedWithAtom,
  finCalcInteractedWithStorageAtom,
} from '../Atoms/FinCalcInteractedWith.atom';
import { generateLeadTrackingData } from '../Tracking/FincalcLead.tracking';
import { activeLeadFormAtom } from '@core/Atoms/Leads/ActiveLeadForm.atom';
import { openFinanceLeadModalAtom } from '@core/Atoms/FinanceModal/FinanceModal.atom';
import { navigateToCheckout } from '@core/Utils/Navigation/CheckoutNavigation';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';
import { Locale } from '@core/Entities/Locale/Locale.entity';
import { useRouter } from 'next/navigation';
import { currentVehicleAtom } from '@core/Atoms/Vehicle/CurrentVehicle.atom';
import { flagsAtom } from '@growthBookExperimentation/Atoms/Flags.atom';
import { isHeycarConnectDealer } from '@core/Utils/Vehicle/IsHeycarConnect';
import { isTspVehicle } from '@core/Utils/Vehicle/isTspVehicle';
import { isVtiVehicle } from '@core/Utils/Vehicle/IsVtiVehicle';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { mapSelectedProductToLeadSubtype } from '@core/Utils/Leads/MapSelectedProductToLeadSubtype';
import { selectedProductIdAtom } from '../Atoms/SelectedProductId.atom';
import { currentVehicleIdAtom } from '@core/Atoms/Vehicle/CurrentVehicleId.atom';

type UseNavigateToCheckout = {
  locale: Locale;
};

export const useNavigateToCheckout = ({ locale }: UseNavigateToCheckout) => {
  const router = useRouter();
  const vehicle = useAtomValue(currentVehicleAtom)!;
  const flags = useAtomValue(flagsAtom);

  const setClickedApplyFinance = useSetAtom(hasClickedApplyFinanceAtom);
  const setFinCalcInteractedWith = useSetAtom(finCalcInteractedWithAtom);
  const setFinCalcInteractedWithStorage = useSetAtom(finCalcInteractedWithStorageAtom);
  const toggleLeadForm = useSetAtom(activeLeadFormAtom);
  const triggerOpenModal = useSetAtom(openFinanceLeadModalAtom);

  const hasHeyCarConnect = flags.heycarConnect && isHeycarConnectDealer(vehicle);
  const hasDealerCash =
    isTspVehicle(vehicle?.details?.dataSource) &&
    vehicle.eligibleProducts.includes('DE_DEALER_CASH');
  const hasVtiCash =
    isVtiVehicle(vehicle?.details?.dataSource) && vehicle.eligibleProducts.includes('DE_D2C_CASH');

  const searchParams = useAtomValue(searchParamsAtom);
  const userId = useAtomValue(userIdAtom);
  const formState = useAtomValue(formStateAtom);
  const listingId = useAtomValue(currentVehicleIdAtom);

  const selectedProductId = useAtomValue(selectedProductIdAtom);
  const isLeasing = mapSelectedProductToLeadSubtype(selectedProductId) === 'leasing';
  const leadFormId = locale === 'fr' && isLeasing ? 'leasing_quote' : 'finance_quote';

  const handleNavigateToCheckoutClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setClickedApplyFinance(true);
    setFinCalcInteractedWithStorage(true);

    if (!navigateToCheckout(router, locale, vehicle, hasHeyCarConnect, hasDealerCash, hasVtiCash)) {
      setFinCalcInteractedWith(true);
      const {
        leadStart: { event, context },
      } = generateLeadTrackingData({
        searchParams,
        userId,
        formState,
        leadFormId,
        listingId,
        vehicle,
      });
      trackCustomEvent({ event, context });
      flags.refactoredLeadForms ? toggleLeadForm('message-dealer-finance') : triggerOpenModal(true);
    }
  };

  return { handleNavigateToCheckoutClick };
};
